<template>
    <div class="container-fluid relative bg-white">

        <div class="flex items-center self-center sm:absolute sm:top-0 sm:left-0 sm:right-0">
            <div @click="handlePrevious" :class="{'btn-disabled': !hasPrevious()}" class="btn xs:btn-sm btn-blue btn-radius-none w-16 xs:w-full">
                <div class="my-px py-2 xs:py-0 text-xl xs:text-xs">
                    <i class="fas fa-arrow-left"></i>
                    <span class="sm:hidden w-full xs:w-16 inline-block text-left ml-1">Previous</span>
                </div>
            </div>
            <div @click="handleNext" :class="{'btn-disabled': !hasNext()}" class="btn xs:btn-sm btn-blue btn-radius-none ml-auto w-16 xs:w-full">
                <div class="my-px py-2 xs:py-0 text-xl xs:text-xs">
                    <span class="sm:hidden w-full xs:w-16 inline-block text-right mr-1">Next</span>
                    <i class="fas fa-arrow-right"></i>
                </div>
            </div>
        </div>

        <div class="mx-16 xs:m-0 border-l border-r xs:border-0 relative">
            <div class="mt-0">
                <div class="bg-tan-200 border-b border-gray-200 px-2 py-1 sm:pt-4">
                    <div class="flex xs:flex-col sm:flex-col md:flex-row sm:items-start md:items-center w-full">
                        <div class="inline-block xs:mb-2 sm:mb-2 mb-0 pr-2 xs:pr-0">
                            <span class="text-uppercase font-bold mr-1 mt-2 text-sm">Go to:</span>
                            <div class="sm:inline-flex">
                                <select v-model="currentFlipChartItem" @change="handleChangeChart" class="form-control form-control-sm align-bottom bg-white">
                                    <option :value="pItem" v-for="pItem in items">{{ pItem.symbol_display || pItem.title || pItem.symbol }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="inline-block md:mx-auto xs:mb-2 sm:mb-2 mb-0 pr-2 xs:pr-0">
                            <span class="text-uppercase font-bold mr-1 mt-2 text-sm">Period:</span>
                            <div class="sm:inline-flex">
                                <select @change="handleApplyAggregation" v-model="aggregationModel" class="form-control form-control-sm align-bottom bg-white">
                                    <option :value="pAggregation" v-for="pAggregation in allAggregations">{{ pAggregation.label }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="inline-block xs:mb-2 sm:mb-2 mb-0">
                            <span class="text-uppercase font-bold mr-1 mt-2 text-sm">Template:</span>
                            <div class="sm:inline-flex">
                                <select class="form-control form-control-sm align-bottom bg-white" v-model="selectedTemplate" @change="handleChangeTemplate">
                                    <option :value="null">None</option>
                                    <optgroup label="Your Templates">
                                        <option :value="pTemplate" v-for="pTemplate in props.templates">{{ pTemplate.name }}</option>
                                    </optgroup>
                                    <optgroup label="Jim's Templates">
                                        <option :value="pjTemplate" v-for="pjTemplate in props.publicTemplates">{{ pjTemplate.name }}</option>
                                    </optgroup>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <spinner v-if="dataLoading"></spinner>

            <div v-if="tooltip !== 'standard'" id="chartPlace">
                <div id="chart-tooltip" class="w-full mt-4 mx-4 flipChartTooltip"></div>
            </div>

            <div :class="{hidden: dataLoading}" id="chart"></div>

            <div :class="{hidden: dataLoading}" class="mt-4 table-responsive border-t-2 border-blue-600">
                <trend-seeker-table v-if="!dataLoading" id="chartTrendSeekerTable" :symbol="currentFlipChartItem.symbol"></trend-seeker-table>
            </div>

            <div class="w-full mt-4" v-if="currentFlipChartItem.notes">
                <div class="bg-tan-200 border-t-2 border-blue-600">
                    <div class="border px-3 py-1 text-sm">
                        <strong>Chart Notes</strong>
                    </div>
                </div>
                <div class="bg-white px-4 py-2 border-b text-sm">
                    {{ currentFlipChartItem.notes }}
                </div>
            </div>

        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeMount, nextTick } from 'vue';

import chartAggregations from '../chart-aggregations';
import TrendSeekerTable from './TrendSeekerTable.vue';

import { useInitFeed } from './Chart/mixins/InitFeed';
import { useRequestInlineTemplate } from '../mixins/RequestInlineTemplate';

const props = defineProps({
    templates: {
        type: Array,
        required: true
    },
    publicTemplates: {
        type: Array,
        required: true
    },
    items: {
        type: Array,
        required: true
    },
    defaultTemplate: Object,
    defaultMode: String
});

// global chart iunstances
let gCharts = null;
let gChart = null;

const currentFlipChartItem = ref(props.items[0]);
const currentChartDefinition = ref(false);

const allAggregations = chartAggregations;
const aggregationModel = ref(allAggregations[0]);

const currentIndex = ref(0);
const selectedTemplate = ref(props.defaultTemplate || null);
const quotes = ref({});

const dataLoading = ref(true);
const defaultFeedMode = ref('realtime');
const tooltip = ref('cards');

// Whether we should only be displaying tools
// or the entire chart definition (used only from flipcharts in "My Chart Book")
// Either 'onlyTools' or 'chartDefinition'
const mode = ref('chartDefinition');

const requestInlineTemplate = useRequestInlineTemplate();
const { initFeed } = useInitFeed(defaultFeedMode.value);

const handleChangeChart = () => {
    dataLoading.value = true;
    currentIndex.value = props.items.findIndex(i => JSON.stringify(i) === JSON.stringify(currentFlipChartItem.value));

    Promise.all([
        requestInlineTemplate.requestChartInlineTemplate(currentFlipChartItem.value.symbol),
        requestChartDefinition(currentFlipChartItem.value.symbol),
        requestQuotes(currentFlipChartItem.value.symbol)
    ]).then(() => {
        dataLoading.value = false;

        handleChangeTemplate();
        updateMainPlot(currentFlipChartItem.value.symbol);
        setChartTitle();
    });
};

const updateMainPlot = (symbol) => {
    gChart.change({
        id: 'MainPlot',
        context: {
            symbol
        }
    });
};

const getTooltipFromTemplate = (template) => {
    if (!template.template.display.tooltip.visible) {
        return 'hidden';
    } else {
        return template.template.display.tooltip.mode;
    }
};

const handleApplyAggregation = () => {
    requestChartDefinition(currentFlipChartItem.value.symbol)
        .then(() => {
            handleChangeTemplate();

            nextTick(() => {
                gChart.change({
                    id: 'Aggregation',
                    context: aggregationModel.value.aggregation
                });

                updateMainPlot(currentFlipChartItem.value.symbol);
            });
        });
};

const applyTemplate = (template) => {
    gChart.load(JSON.stringify(template.template), {
        keepOldAnnotations: true,
        keepNewAnnotations: false,
        keepOldAggregation: true
    });

    tooltip.value = getTooltipFromTemplate(template);
};

const handleChangeTemplate = () => {
    // If we have a chart, load it in attempting to display the tools
    if (currentChartDefinition.value && currentChartDefinition.value.state) {
        const currentDefinition = gChart.asTemplate({ main: true });

        console.log('Loading chart defintion', JSON.parse(currentChartDefinition.value.state));

        gChart.load(currentChartDefinition.value.state, {
            keepOldAnnotations: false,
            keepNewAnnotations: true,
            keepOldAggregation: true,
        });

        console.log('Loading previous chart defintion', currentDefinition);

        gChart.load(JSON.stringify(currentDefinition), {
            keepOldAnnotations: true,
            keepNewAnnotations: false,
            keepOldAggregation: true,
        });
    } else {
        const defaultTemplate = gChart.getDefaultTemplate();

        gChart.load(JSON.stringify(defaultTemplate), {
            keepOldAnnotations: false,
            keepNewAnnotations: true,
            keepOldAggregation: true,
        })
    }

    if (selectedTemplate.value) {
        applyTemplate(selectedTemplate.value);
    }

    updateMainPlot(currentFlipChartItem.value.symbol);
};

const setChartTitle = () => {
    gChart.setTitle({
        text: `${currentFlipChartItem.value.symbol_display || currentFlipChartItem.value.title} - CommodityTrends`,
        align: 'left',
        x: 25,
        y: 25,
        style: {"fontSize": "18px","color":"#888888"},
        floating: true
    });
};

const getState = () => {
    if (currentChartDefinition.value && currentChartDefinition.value.state) {
        return {
            template: currentChartDefinition.value.state,
            symbol: currentFlipChartItem.value.symbol
        };
    }

    return {
        symbol: currentFlipChartItem.value.symbol
    };
};

const requestQuotes = (symbol) => {
    const route = window.route('api.quotes');

    return axios.post(route, {
        raw: true,
        symbol,
        fields: 'symbol,symbolName,symbolType,contractName,contractMonthYear',
        after: 'getChartTitle'
    }).then((response) => {
        quotes.value = response.data.data[0];
    }).catch((error) => {
        console.error(error);
    });
};

const requestChartDefinition = (symbol) => {
    if (currentFlipChartItem.value.state) {
        currentChartDefinition.value = currentFlipChartItem.value;
        return Promise.resolve();
    }

    const route = window.route('interactive-charts.definition-by-period-and-spec', { symbol });
    const period = getPeriodFromUnit(aggregationModel.value.aggregation.unit);
    const spec = aggregationModel.value.aggregation.spec === 'Nearest' ? 'nearby' : 'contract';
    const params = { period, spec };

    return axios.post(route, params)
        .then((response) => {
            if (response.data.id) {
                currentChartDefinition.value = response.data;
            } else {
                currentChartDefinition.value = false;
            }
        }).catch((error) => {
            // TODO
            debugger;
        })
};

const requestFeedPreference = () => {
    const route = window.route('api.preferences.show', {name: 'default_feed_mode'});

    return axios.get(route)
        .then((response) => {
            if (response.data) {
                defaultFeedMode.value = response.data;
            }
        }).catch((error) => {
            console.error(error);
        });
};

const getPeriodFromUnit = (unit) => {
    switch (unit) {
        case 'Intraday':
            return 'intraday';
        case 'Day':
            return 'daily';
        case 'Week':
            return 'weekly';
        case 'Month':
            return 'monthly';
        default:
            throw new Error(`Aggregation unit [${unit}] not found`);
    }
};

const hasPrevious = () => {
    return currentIndex.value - 1 >= 0;
};

const hasNext = () => {
    return currentIndex.value + 1 < props.items.length;
};

const handleNext = () => {
    currentIndex.value += 1;
    currentFlipChartItem.value = props.items[currentIndex.value];
    handleChangeChart();
};

const handlePrevious = () => {
    currentIndex.value -= 1;
    currentFlipChartItem.value = props.items[ currentIndex.value ];
    handleChangeChart();
};

onBeforeMount(() => {
    gCharts = window.Barchart.RealtimeWidgets.Charts;

    const gFeed = initFeed();

    const promises = [
        requestInlineTemplate.requestChartInlineTemplate(currentFlipChartItem.value.symbol),
        requestChartDefinition(currentFlipChartItem.value.symbol),
        requestFeedPreference()
    ];

    Promise.all(promises)
        .then(() => {
            dataLoading.value = false;
            gFeed.ready().then((done) => {
                gChart = gFeed.addChart('chart', getState());

                setChartTitle();

                if (props.defaultTemplate) {
                    console.log('Applying default template')
                    applyTemplate(props.defaultTemplate);
                    updateMainPlot(currentFlipChartItem.value.symbol);
                }
            });

            requestQuotes(currentFlipChartItem.value.symbol);
        });
});
</script>

<style lang="scss">
.flipChartTooltip {
    .value-title {
        @apply font-bold;
    }

    .field-name {
        @apply font-bold;
    }
}

#chart {
    height: 600px;

    *:focus {
        outline: none;
    }

    .bcharts-card {
        opacity: .8;
    }

    .bcharts-card.bcharts-card-first {
        top: 50px !important;
    }

    .plot {
        display: inline-block;
        background: #f2f2f2;
        @apply p-1 mb-1 rounded;

        .value-title,
        .field-name,
        .field-value {
            @apply text-sm;
        }

        .curve-color {
            margin-right: 2px;
        }

        .value-title {
            @apply mr-1 cursor-pointer;
        }
    }

    .move-pane {
        display: inline-block;
        @apply p-1 mr-1 cursor-pointer rounded;
        background: #f2f2f2;
    }

    .delete-plot {
        @apply p-1 mr-1 cursor-pointer;
        svg {
            fill: #e3342f;
        }
    }
}
</style>
